import React, { useEffect, useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";
import { addItem, getItems, removeItem, updateItem } from "../storage";
import "../StockInward.css";

const IMPORT_HEADER_ALIASES = {
  code: ["code", "item code", "itemcode", "product code", "productcode"],
  name: ["name", "item name", "itemname", "product name", "productname"],
  itemGroup: ["group", "item group", "itemgroup", "product group", "productgroup"],
  purchasePrice: ["purchase price", "purchaseprice", "buy price", "buying price", "cost"],
  sellingPrice: ["selling price", "sellingprice", "sale price", "saleprice"],
};

const REQUIRED_IMPORT_FIELDS = ["code", "name", "itemGroup"];
const PAGE_SIZE = 20;
const EMPTY_SEARCH_FILTERS = {
  code: "",
  name: "",
  itemGroup: "",
  purchasePrice: "",
  sellingPrice: "",
};
const EXPORT_HEADERS = ["code", "product name", "group", "purchase price", "selling price"];

const NORMALIZED_IMPORT_ALIASES = Object.keys(IMPORT_HEADER_ALIASES).reduce((acc, key) => {
  acc[key] = IMPORT_HEADER_ALIASES[key].map((label) => normalizeHeader(label));
  return acc;
}, {});

function normalizeHeader(value) {
  return String(value || "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
}

function isSpreadsheetFile(file) {
  const name = String(file?.name || "").toLowerCase();
  return name.endsWith(".xlsx") || name.endsWith(".xls");
}

async function readRowsFromFile(file) {
  if (isSpreadsheetFile(file)) {
    const data = await file.arrayBuffer();
    const workbook = XLSX.read(data, { type: "array" });
    const sheetName = workbook.SheetNames[0];
    if (!sheetName) return [];
    const sheet = workbook.Sheets[sheetName];
    return XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "", blankrows: false });
  }

  const text = await file.text();
  return parseCsv(text);
}

function parseCsv(text) {
  const rows = [];
  let row = [];
  let field = "";
  let inQuotes = false;

  for (let i = 0; i < text.length; i++) {
    const char = text[i];

    if (char === '"') {
      if (inQuotes && text[i + 1] === '"') {
        field += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if (char === "," && !inQuotes) {
      row.push(field);
      field = "";
      continue;
    }

    if ((char === "\n" || char === "\r") && !inQuotes) {
      if (char === "\r" && text[i + 1] === "\n") {
        i += 1;
      }
      row.push(field);
      rows.push(row);
      row = [];
      field = "";
      continue;
    }

    field += char;
  }

  row.push(field);
  rows.push(row);
  if (rows.length > 0 && rows[0].length > 0) {
    rows[0][0] = rows[0][0].replace(/^\uFEFF/, "");
  }
  return rows;
}

function buildHeaderMap(headerRow) {
  const normalized = headerRow.map(normalizeHeader);
  const map = {};
  Object.keys(NORMALIZED_IMPORT_ALIASES).forEach((key) => {
    const idx = normalized.findIndex((value) => NORMALIZED_IMPORT_ALIASES[key].includes(value));
    if (idx >= 0) {
      map[key] = idx;
    }
  });
  return map;
}

export default function ItemMaster() {
  const [form, setForm] = useState({
    code: "",
    name: "",
    itemGroup: "",
    purchasePrice: "",
    sellingPrice: "",
  });
  const [items, setItems] = useState([]);
  const [searchFilters, setSearchFilters] = useState(EMPTY_SEARCH_FILTERS);
  const [importFileName, setImportFileName] = useState("");
  const [importRows, setImportRows] = useState([]);
  const [importErrors, setImportErrors] = useState([]);
  const [importFailures, setImportFailures] = useState([]);
  const [importStatus, setImportStatus] = useState("");
  const [importing, setImporting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [page, setPage] = useState(1);
  const formRef = useRef(null);

  const refresh = async () => {
    try {
      const list = await getItems();
      setItems(list || []);
    } catch (err) {
      console.error(err);
      alert("Failed to load items.");
    }
  };
  useEffect(() => { refresh(); }, []);

  const filtered = useMemo(() => {
    const codeFilter = String(searchFilters.code || "").trim().toLowerCase();
    const nameFilter = String(searchFilters.name || "").trim().toLowerCase();
    const groupFilter = String(searchFilters.itemGroup || "").trim().toLowerCase();
    const purchasePriceFilter = String(searchFilters.purchasePrice || "").trim().toLowerCase();
    const sellingPriceFilter = String(searchFilters.sellingPrice || "").trim().toLowerCase();

    return (Array.isArray(items) ? items : []).filter((item) => {
      if (codeFilter && !String(item.code || "").toLowerCase().includes(codeFilter)) return false;
      if (nameFilter && !String(item.name || "").toLowerCase().includes(nameFilter)) return false;
      if (groupFilter && !String(item.itemGroup || "").toLowerCase().includes(groupFilter)) return false;
      if (
        purchasePriceFilter &&
        !Number(item.purchasePrice || 0).toFixed(2).toLowerCase().includes(purchasePriceFilter)
      ) {
        return false;
      }
      if (
        sellingPriceFilter &&
        !Number(item.sellingPrice || 0).toFixed(2).toLowerCase().includes(sellingPriceFilter)
      ) {
        return false;
      }
      return true;
    });
  }, [items, searchFilters]);

  const sortedItems = useMemo(() => {
    const list = [...filtered];
    list.sort((a, b) =>
      String(a.code || "").localeCompare(String(b.code || ""), undefined, { numeric: true, sensitivity: "base" })
    );
    return list;
  }, [filtered]);

  const pageCount = Math.max(1, Math.ceil(sortedItems.length / PAGE_SIZE));
  const pageItems = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return sortedItems.slice(start, start + PAGE_SIZE);
  }, [sortedItems, page]);

  useEffect(() => {
    setPage(1);
  }, [searchFilters]);

  const hasActiveSearchFilters = useMemo(
    () => Object.values(searchFilters).some((value) => String(value || "").trim() !== ""),
    [searchFilters]
  );

  useEffect(() => {
    setPage((current) => Math.min(current, pageCount));
  }, [pageCount]);

  const onSubmit = async (e) => {
    e.preventDefault();
    const code = form.code.trim();
    const name = form.name.trim();
    if (!code || !name) return;
    const itemGroup = form.itemGroup.trim();
    const purchasePrice = Number(form.purchasePrice) || 0;
    const sellingPrice = Number(form.sellingPrice) || 0;
    const itemType = "common";
    try {
      if (editingId) {
        await updateItem(editingId, { code, name, itemGroup, purchasePrice, sellingPrice, itemType });
      } else {
        await addItem({ code, name, itemGroup, purchasePrice, sellingPrice, itemType });
      }
      setForm({ code: "", name: "", itemGroup: "", purchasePrice: "", sellingPrice: "" });
      setEditingId(null);
      await refresh();
    } catch (err) {
      console.error(err);
      alert("Failed to save item.");
    }
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setForm({
      code: item.code || "",
      name: item.name || "",
      itemGroup: item.itemGroup || "",
      purchasePrice: item.purchasePrice === null || item.purchasePrice === undefined ? "" : String(item.purchasePrice),
      sellingPrice: item.sellingPrice === null || item.sellingPrice === undefined ? "" : String(item.sellingPrice),
    });
    if (formRef.current) {
      formRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm({ code: "", name: "", itemGroup: "", purchasePrice: "", sellingPrice: "" });
  };

  const handleImportFile = async (event) => {
    const file = event.target.files && event.target.files[0];
    setImportErrors([]);
    setImportFailures([]);
    setImportRows([]);
    setImportStatus("");
    if (!file) {
      setImportFileName("");
      return;
    }

    setImportFileName(file.name);
    try {
      const rows = (await readRowsFromFile(file))
        .map((row) => row.map((cell) => String(cell || "").trim()))
        .filter((row) => row.some((cell) => cell !== ""));

      if (rows.length === 0) {
        setImportErrors([{ line: 0, message: "No data rows found in file." }]);
        return;
      }

      const headerMap = buildHeaderMap(rows[0]);
      const hasAnyHeader = Object.keys(headerMap).length > 0;
      const hasHeader = REQUIRED_IMPORT_FIELDS.every((key) => headerMap[key] !== undefined);
      if (hasAnyHeader && !hasHeader) {
        setImportErrors([
          { line: 0, message: "Missing required columns: code, product name, group." },
        ]);
        return;
      }
      const dataStart = hasHeader ? 1 : 0;
      const map = hasHeader
        ? headerMap
        : { code: 0, name: 1, itemGroup: 2, purchasePrice: 3, sellingPrice: 4 };

      const parsed = [];
      const errors = [];

      for (let i = dataStart; i < rows.length; i++) {
        const row = rows[i];
        if (row.every((cell) => cell === "")) {
          continue;
        }

        const code = (row[map.code] || "").trim();
        const name = (row[map.name] || "").trim();
        const itemGroup = (row[map.itemGroup] || "").trim();
        const purchaseRaw = (row[map.purchasePrice] || "").trim();
        const sellingRaw = (row[map.sellingPrice] || "").trim();
        const lineNumber = i + 1;
        const missing = [];
        if (!code) missing.push("code");
        if (!name) missing.push("product name");
        if (!itemGroup) missing.push("group");
        if (missing.length > 0) {
          errors.push({
            line: lineNumber,
            message: `Missing ${missing.join(", ")}.`,
          });
          continue;
        }

        let purchasePrice = 0;
        if (purchaseRaw) {
          const parsedPrice = Number(purchaseRaw);
          if (Number.isNaN(parsedPrice)) {
            errors.push({ line: lineNumber, message: "Invalid purchase price." });
            continue;
          }
          purchasePrice = parsedPrice;
        }

        let sellingPrice = 0;
        if (sellingRaw) {
          const parsedPrice = Number(sellingRaw);
          if (Number.isNaN(parsedPrice)) {
            errors.push({ line: lineNumber, message: "Invalid selling price." });
            continue;
          }
          sellingPrice = parsedPrice;
        }

        parsed.push({
          code,
          name,
          itemGroup,
          purchasePrice,
          sellingPrice,
        });
      }

      if (parsed.length === 0 && errors.length === 0) {
        errors.push({ line: 0, message: "No valid rows found to import." });
      }

      setImportRows(parsed);
      setImportErrors(errors);
    } catch (err) {
      console.error(err);
      setImportErrors([{ line: 0, message: "Failed to read the file." }]);
    }
  };

  const handleImport = async () => {
    if (importing) return;
    if (importErrors.length > 0) {
      alert("Please fix the import errors before importing.");
      return;
    }
    if (importRows.length === 0) {
      alert("No items to import.");
      return;
    }

    setImporting(true);
    setImportStatus("Importing items...");
    setImportFailures([]);

    let successCount = 0;
    const failures = [];
    for (const row of importRows) {
      try {
        await addItem({ ...row, itemType: "common" });
        successCount += 1;
      } catch (err) {
        failures.push({
          code: row.code,
          message: err?.message || "Failed to import item.",
        });
      }
    }

    await refresh();
    setImporting(false);
    if (failures.length > 0) {
      setImportStatus(`Imported ${successCount} items. ${failures.length} failed.`);
    } else {
      setImportStatus(`Imported ${successCount} items.`);
    }
    setImportFailures(failures);
  };

  const handleExport = () => {
    if (sortedItems.length === 0) {
      alert("No items to export.");
      return;
    }

    const rows = sortedItems.map((item) => [
      String(item.code || "").trim(),
      String(item.name || "").trim(),
      String(item.itemGroup || "").trim(),
      Number(item.purchasePrice || 0),
      Number(item.sellingPrice || 0),
    ]);

    const worksheet = XLSX.utils.aoa_to_sheet([EXPORT_HEADERS, ...rows]);
    worksheet["!cols"] = [
      { wch: 18 },
      { wch: 28 },
      { wch: 22 },
      { wch: 18 },
      { wch: 18 },
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      "Common Items"
    );
    XLSX.writeFile(workbook, "item-master-export.xlsx");
  };

  return (
    <div className="stock-inward-container" style={{ paddingTop: 0 }}>
      <div className="form-card" ref={formRef}>
        <div className="card-header">
          <h2 className="card-title">Item Master</h2>
          <div style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
            <div className="entry-count">Shared for Purchase and Sales</div>
            {editingId ? <div className="entry-count">Editing</div> : null}
          </div>
        </div>
        <form className="inward-form" onSubmit={onSubmit}>
          <div className="form-grid">
            <div className="field fl-field">
              <input
                id="item-code"
                className="fl-input input-field"
                type="text"
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
                placeholder=" "
                required
              />
              <label htmlFor="item-code" className="fl-label">Item Code</label>
            </div>
            <div className="field fl-field">
              <input
                id="item-name"
                className="fl-input input-field"
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder=" "
                required
              />
              <label htmlFor="item-name" className="fl-label">Item Name</label>
            </div>
            <div className="field fl-field">
              <input
                id="item-group"
                className="fl-input input-field"
                type="text"
                value={form.itemGroup}
                onChange={(e) => setForm({ ...form, itemGroup: e.target.value })}
                placeholder=" "
              />
              <label htmlFor="item-group" className="fl-label">Item Group</label>
            </div>
            <div className="field fl-field">
              <input
                id="purchase-price"
                className="fl-input input-field numeric"
                type="number"
                inputMode="decimal"
                step="any"
                value={form.purchasePrice}
                onChange={(e) => setForm({ ...form, purchasePrice: e.target.value })}
                placeholder=" "
              />
              <label htmlFor="purchase-price" className="fl-label">Purchase Price</label>
            </div>
            <div className="field fl-field">
              <input
                id="selling-price"
                className="fl-input input-field numeric"
                type="number"
                inputMode="decimal"
                step="any"
                value={form.sellingPrice}
                onChange={(e) => setForm({ ...form, sellingPrice: e.target.value })}
                placeholder=" "
              />
              <label htmlFor="selling-price" className="fl-label">Selling Price</label>
            </div>
          </div>
          <div className="form-actions">
            {editingId ? (
              <button type="button" className="btn-secondary" onClick={cancelEdit}>
                Cancel
              </button>
            ) : null}
            <button type="submit" className="btn-primary">
              {editingId ? "Update Item" : "Add Item"}
            </button>
          </div>
        </form>
      </div>

      <div className="tab-pane-anim">

      <div className="form-card">
        <div className="card-header">
          <h2 className="card-title">Import Items</h2>
        </div>
        <div className="inward-form">
          <div className="form-grid">
            <div className="field">
              <label>CSV/XLSX File</label>
              <input
                type="file"
                accept=".csv,.xlsx,.xls,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                className="input-field"
                onChange={handleImportFile}
              />
            </div>
            <div className="field">
              <label>Required Columns</label>
              <div style={{ color: "#475569", fontSize: "0.875rem" }}>
                Code, Product Name, Group (case-insensitive). Optional: Purchase Price, Selling Price.
              </div>
              <div style={{ color: "#94a3b8", fontSize: "0.8rem", marginTop: "6px" }}>
                CSV or XLSX. First sheet only. Example header: code, product name, group, purchase price, selling price
              </div>
            </div>
          </div>

          {importFileName ? (
            <div style={{ marginTop: "12px", color: "#475569" }}>
              File: {importFileName}
            </div>
          ) : null}
          {importRows.length > 0 ? (
            <div style={{ marginTop: "6px", color: "#475569" }}>
              Ready to import: {importRows.length} items
            </div>
          ) : null}
          {importErrors.length > 0 ? (
            <div style={{ marginTop: "8px", color: "#b91c1c", fontSize: "0.875rem" }}>
              {importErrors.slice(0, 5).map((err, idx) => (
                <div key={`${err.line}-${idx}`}>
                  {err.line ? `Line ${err.line}: ${err.message}` : err.message}
                </div>
              ))}
              {importErrors.length > 5 ? (
                <div>And {importErrors.length - 5} more errors...</div>
              ) : null}
            </div>
          ) : null}
          {importStatus ? (
            <div style={{ marginTop: "8px", color: "#1f2937", fontSize: "0.875rem" }}>
              {importStatus}
            </div>
          ) : null}
          {importFailures.length > 0 ? (
            <div style={{ marginTop: "6px", color: "#b91c1c", fontSize: "0.875rem" }}>
              {importFailures.slice(0, 5).map((fail, idx) => (
                <div key={`${fail.code}-${idx}`}>
                  {fail.code ? `${fail.code}: ${fail.message}` : fail.message}
                </div>
              ))}
              {importFailures.length > 5 ? (
                <div>And {importFailures.length - 5} more failures...</div>
              ) : null}
            </div>
          ) : null}

          <div className="form-actions" style={{ marginTop: "12px" }}>
            <button type="button" className="btn-primary" onClick={handleImport} disabled={importing}>
              {importing ? "Importing..." : "Import Items"}
            </button>
          </div>
        </div>
      </div>

      <div className="entries-card">
        <div className="card-header" style={{ borderBottom: "none", gap: 12, flexWrap: "wrap" }}>
          <h2 className="card-title">Common Items</h2>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            {hasActiveSearchFilters ? (
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setSearchFilters(EMPTY_SEARCH_FILTERS)}
                style={{ padding: "0.35rem 0.7rem", fontSize: "0.75rem" }}
              >
                Clear Filters
              </button>
            ) : null}
            <button
              type="button"
              className="btn-secondary"
              onClick={handleExport}
              style={{ padding: "0.35rem 0.7rem", fontSize: "0.75rem" }}
            >
              Export
            </button>
          </div>
        </div>
        <div className="inward-form" style={{ paddingTop: 0 }}>
          <div className="form-grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))" }}>
            <div className="field">
              <label>Item Code</label>
              <input
                type="search"
                className="input-field"
                placeholder="Search item code"
                value={searchFilters.code}
                onChange={(e) => setSearchFilters((prev) => ({ ...prev, code: e.target.value }))}
              />
            </div>
            <div className="field">
              <label>Item Name</label>
              <input
                type="search"
                className="input-field"
                placeholder="Search item name"
                value={searchFilters.name}
                onChange={(e) => setSearchFilters((prev) => ({ ...prev, name: e.target.value }))}
              />
            </div>
            <div className="field">
              <label>Item Group</label>
              <input
                type="search"
                className="input-field"
                placeholder="Search item group"
                value={searchFilters.itemGroup}
                onChange={(e) => setSearchFilters((prev) => ({ ...prev, itemGroup: e.target.value }))}
              />
            </div>
            <div className="field">
              <label>Purchase Price</label>
              <input
                type="search"
                className="input-field"
                placeholder="Search purchase price"
                value={searchFilters.purchasePrice}
                onChange={(e) => setSearchFilters((prev) => ({ ...prev, purchasePrice: e.target.value }))}
              />
            </div>
            <div className="field">
              <label>Selling Price</label>
              <input
                type="search"
                className="input-field"
                placeholder="Search selling price"
                value={searchFilters.sellingPrice}
                onChange={(e) => setSearchFilters((prev) => ({ ...prev, sellingPrice: e.target.value }))}
              />
            </div>
          </div>
        </div>
        <div className="entries-table-container">
          <table className="entries-table">
            <colgroup>
            <col style={{ width: "160px" }} />
            <col style={{ width: "220px" }} />
            <col style={{ width: "180px" }} />
            <col style={{ width: "140px" }} />
            <col style={{ width: "140px" }} />
            <col style={{ width: "140px" }} />
          </colgroup>
          <thead>
            <tr>
              <th>Item Code</th>
              <th>Item Name</th>
              <th>Item Group</th>
              <th className="text-right">Purchase Price</th>
              <th className="text-right">Selling Price</th>
              <th style={{ width: 140 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={6} className="empty-state">No items yet</td>
              </tr>
            ) : (
              pageItems.map((item) => (
                <tr key={item.id}>
                  <td>{item.code}</td>
                  <td>{item.name}</td>
                  <td>{item.itemGroup || "-"}</td>
                  <td className="text-right">{Number(item.purchasePrice || 0).toFixed(2)}</td>
                  <td className="text-right">{Number(item.sellingPrice || 0).toFixed(2)}</td>
                  <td className="item-actions">
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={() => startEdit(item)}
                      style={{ padding: "0.35rem 0.7rem", fontSize: "0.75rem" }}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className="btn-remove"
                      title="Delete"
                      onClick={async () => {
                        if (window.confirm("Delete item?")) {
                          try {
                            await removeItem(item.id);
                            await refresh();
                          } catch (err) {
                            console.error(err);
                            alert("Failed to delete item.");
                          }
                        }
                      }}
                    >
                      x
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
          </table>
          {sortedItems.length > 0 ? (
            <div className="form-actions" style={{ justifyContent: "space-between", marginTop: "12px" }}>
              <div style={{ color: "#64748b", fontSize: "0.875rem" }}>
                Showing {(page - 1) * PAGE_SIZE + 1}-{Math.min(page * PAGE_SIZE, sortedItems.length)} of {sortedItems.length}
              </div>
              <div style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  Prev
                </button>
                <span style={{ color: "#475569", fontSize: "0.875rem" }}>
                  Page {page} of {pageCount}
                </span>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                  disabled={page === pageCount}
                >
                  Next
                </button>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      </div>
    </div>
  );
}
